import { memo, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { setCurrentQuestionInfo } from '@actions/quizTaking';
import { Spacings } from '@styles/index';
import Saved from '@icon/Saved';
import { scrollIntoViewIfNeeded } from '@cont/Quiz/utils';
import useQuestionStatus from '../Common/useQuestionStatus';

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  cursor: ${({ $isLocked }) => ($isLocked ? 'not-allowed' : 'pointer')};
  opacity: ${({ $isLocked }) => ($isLocked ? 0.3 : 1)};

  transform: background 400ms ease-in;
  height: ${Spacings.SPACING_10B};
  width: ${Spacings.SPACING_10B};
  border-radius: ${Spacings.SPACING_1B};

  ${({ $isAttempted, $isSelected }) => {
    if ($isAttempted && $isSelected) {
      return `
        border: 1px solid var(--color-text-primary);
        color: var(--color-i-green);
        background: var(--color-i-light-green);
      `;
    }

    if ($isAttempted) {
      return `
        border: none;
        color: var(--color-i-green);
        background: var(--color-i-light-green);
      `;
    }

    if ($isSelected) {
      return `
        border: 1px solid var(--color-text-primary);
        background: var(--color-base-1);
      `;
    }

    return `
      border: 1px solid var(--color-divider);
      background: var(--color-base-1);
    `;
  }}
`;

const MarkedIcon = styled(Saved)`
  position: absolute;
  bottom: -${Spacings.SPACING_2B};
  right: -${Spacings.SPACING_2B};
  transform: all 400ms ease-in;
`;

const Question = ({ rank, sectionUID, questionUID, isLocked }) => {
  const dispatch = useDispatch();
  const blockRef = useRef(null);
  const [isClicked, setIsClicked] = useState(false);

  const { isSelected, isAttempted, isMarked } = useQuestionStatus(
    sectionUID,
    questionUID
  );

  useEffect(() => {
    if (!isSelected && isClicked) {
      setIsClicked(false);
    }
  }, [isSelected, isClicked]);

  useEffect(() => {
    if (isSelected && !isClicked && blockRef.current) {
      scrollIntoViewIfNeeded({ target: blockRef.current, paddingInPixel: 50 });
    }
  }, [isSelected, isClicked]);

  const handleOnClick = () => {
    if (isLocked) {
      return;
    }

    setIsClicked(true);

    dispatch(
      setCurrentQuestionInfo({
        sectionUID,
        questionUID
      })
    );
  };

  return (
    <Wrapper
      ref={blockRef}
      $isSelected={isSelected}
      $isAttempted={isAttempted}
      $isLocked={isLocked}
      onClick={handleOnClick}
    >
      {rank}
      {isMarked && (
        <MarkedIcon
          size={Spacings.SPACING_4B}
          color="var(--color-i-orange-1)"
        />
      )}
    </Wrapper>
  );
};

export default memo(Question);

Question.propTypes = {
  rank: PropTypes.number.isRequired,
  sectionUID: PropTypes.string.isRequired,
  questionUID: PropTypes.string.isRequired,
  isLocked: PropTypes.bool.isRequired
};
